import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";
import { breadcrumbLd } from "@/components/seo/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const CATEGORY_META = {
  "ha-filler":       { label: "HA Fillers",        description: "Editorial coverage of hyaluronic-acid dermal fillers." },
  "botox":           { label: "Botulinum Toxins",  description: "Coverage of neurotoxin brands, lots, and sourcing." },
  "skin-booster":    { label: "Skin Boosters",     description: "Bioregenerative skinboosters and polynucleotide therapies." },
  "body-filler":     { label: "Body Fillers",      description: "Collagen biostimulators and body volumizing references." },
  "lipolysis":       { label: "Lipolytics",        description: "Injectable fat-dissolving products and protocols." },
  "exosome":         { label: "Exosomes",          description: "Regenerative exosome treatments and brand landscape." },
  "iv-therapy":      { label: "IV Therapy",        description: "IV / IM injectables for clinic wellness offerings." },
  "lidocaine-cream": { label: "Anesthetic Creams", description: "Topical numbing agents for clinical procedures." },
  "etc":             { label: "Accessories",       description: "PDO threads, cannulas, and ancillary supplies." },
};

const TOPIC_LABELS = {
  product: "Product",
  comparison: "Comparison",
  buying_guide: "Buying guide",
  authenticity: "Authenticity",
  regulatory: "Regulatory",
  news: "News",
  clinical: "Clinical",
  price_guide: "Pricing",
};

const TOPIC_FILTERS = [
  { id: null, label: "All" },
  { id: "product", label: "Spotlights" },
  { id: "comparison", label: "Comparisons" },
  { id: "buying_guide", label: "Buying" },
  { id: "authenticity", label: "Authenticity" },
  { id: "regulatory", label: "Regulatory" },
  { id: "news", label: "News" },
  { id: "clinical", label: "Clinical" },
  { id: "price_guide", label: "Pricing" },
];

export const JournalCategory = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const topic = searchParams.get("topic") || null;

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const meta = CATEGORY_META[slug] || { label: slug, description: "Editorial coverage." };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    setLoading(true);
    const skip = (page - 1) * PAGE_SIZE;
    let url = `${API}/journal/posts?category=${encodeURIComponent(slug)}&limit=${PAGE_SIZE}&skip=${skip}`;
    if (topic) url += `&topic=${encodeURIComponent(topic)}`;
    axios.get(url)
      .then((r) => {
        setPosts(r.data.posts || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { setPosts([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [slug, page, topic]);

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page"); else next.set("page", String(p));
    setSearchParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setTopic = (t) => {
    const next = new URLSearchParams(searchParams);
    if (!t) next.delete("topic"); else next.set("topic", t);
    next.delete("page");
    setSearchParams(next, { replace: false });
  };

  return (
    <>
      <Seo
        title={`${meta.label} Journal — Clinical Guides & Wholesale Insights`}
        description={`Read ${total} clinical guides & wholesale insights on ${meta.label.toLowerCase()}. ${meta.description}`.slice(0, 155)}
        keywords={`${meta.label.toLowerCase()} journal, ${meta.label.toLowerCase()} clinical guide, wholesale ${meta.label.toLowerCase()} buying, ${slug} comparison, ${slug} for clinics, aesthetic medicine ${slug}, ${slug} MOQ 5, discount ${slug} wholesale`}
        path={`/journal/category/${slug}`}
        jsonLd={breadcrumbLd([
          { name: "Home", url: "/" },
          { name: "Journal", url: "/journal" },
          { name: meta.label, url: `/journal/category/${slug}` },
        ])}
      />
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 pt-28 md:pt-32 pb-24" data-testid="journal-category-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-neutral-500 tracking-wide mb-6" data-testid="breadcrumb">
          <Link to="/" className="hover:text-black uppercase tracking-[0.2em]">Home</Link>
          <span>›</span>
          <Link to="/journal" className="hover:text-black uppercase tracking-[0.2em]">Journal</Link>
          <span>›</span>
          <span className="text-black uppercase tracking-[0.2em] font-medium">{meta.label}</span>
        </nav>

        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Journal · Category</div>
        <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tight mb-4">{meta.label}</h1>
        <p className="text-sm text-neutral-600 font-light max-w-2xl mb-8">
          {meta.description} <span className="text-black font-medium">{total} {total === 1 ? "article" : "articles"}</span>
          {totalPages > 1 && <> · page {page} of {totalPages}</>}
        </p>

        {/* Topic filter */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="topic-filter">
          {TOPIC_FILTERS.map((t) => (
            <button
              key={t.id || "all"}
              onClick={() => setTopic(t.id)}
              className={`px-3 py-1.5 text-[11px] font-medium tracking-wide uppercase border transition-colors ${
                (topic || null) === t.id ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"
              }`}
              data-testid={`topic-${t.id || "all"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="py-24 flex items-center justify-center text-neutral-500">
            <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading articles…
          </div>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-neutral-300" data-testid="journal-empty">
            <p className="text-neutral-500">No articles in this category yet.</p>
            <Link to="/journal" className="inline-flex items-center gap-1 mt-3 text-sm font-medium hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Journal home
            </Link>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-neutral-200 border-y border-neutral-200" data-testid="journal-list">
              {posts.map((p) => (
                <li key={p.id}>
                  <Link to={`/journal/${p.slug}`} className="flex gap-4 py-5 group hover:bg-neutral-50 transition-colors px-2 -mx-2" data-testid={`journal-row-${p.id}`}>
                    {/* Compact image */}
                    <img
                      src={p.image}
                      alt={p.image_alt || p.title}
                      className="w-24 h-24 md:w-28 md:h-28 object-cover bg-neutral-100 shrink-0"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
                        <span>{TOPIC_LABELS[p.topic_type] || p.topic_type}</span>
                        <span className="w-1 h-1 bg-neutral-400 rounded-full" />
                        <span>{p.reading_time_min} min</span>
                        {p.is_external_brand && (
                          <>
                            <span className="w-1 h-1 bg-neutral-400 rounded-full" />
                            <span className="text-amber-700">Industry coverage</span>
                          </>
                        )}
                      </div>
                      <h3 className="font-heading text-base md:text-xl font-bold tracking-tight leading-snug mt-1 group-hover:underline underline-offset-4 line-clamp-2">{p.title}</h3>
                      {/* 2-line truncated summary (first lines) */}
                      <p className="text-xs md:text-sm text-neutral-600 font-light mt-1 line-clamp-2">{p.subtitle}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-between mt-10" data-testid="journal-pagination" aria-label="Pagination">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-xs font-bold uppercase tracking-wide disabled:opacity-30 disabled:cursor-not-allowed hover:border-black"
                  data-testid="page-prev"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Prev
                </button>
                <PageNumbers current={page} total={totalPages} onJump={setPage} />
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-xs font-bold uppercase tracking-wide disabled:opacity-30 disabled:cursor-not-allowed hover:border-black"
                  data-testid="page-next"
                >
                  Next <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </nav>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
};

// Compact page numbers: 1 … current-1 current current+1 … last
const PageNumbers = ({ current, total, onJump }) => {
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const list = Array.from(pages).filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const n of list) {
    if (n - prev > 1) out.push("…");
    out.push(n);
    prev = n;
  }
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {out.map((n, i) => (
        n === "…"
          ? <span key={`g-${i}`} className="px-2 text-neutral-400">…</span>
          : <button
              key={n}
              onClick={() => onJump(n)}
              className={`min-w-[32px] h-8 px-2 border tabular-nums ${current === n ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
              data-testid={`page-${n}`}
            >
              {n}
            </button>
      ))}
    </div>
  );
};
